/**
 * Timeline zoom/pan math. State is held by ZapPlayer; functions take it as
 * a context object so they remain pure-ish and testable.
 *
 * @typedef {Object} TimelineState
 * @property {number} duration   video.duration in seconds (read-only at call time)
 * @property {number} zoomLevel  1 = full, >1 = zoomed in (max 200)
 * @property {number} panOffsetT visible-window left edge in seconds
 */

export function visibleDur(state) {
  const dur = state.duration || 0;
  return dur / Math.max(1, state.zoomLevel || 1);
}

export function leftBoundT(state) {
  const dur = state.duration || 0;
  const vd  = visibleDur(state);
  return Math.max(0, Math.min(Math.max(0, dur - vd), state.panOffsetT || 0));
}

export function timeFromX(state, stripEl, clientX) {
  const r = stripEl.getBoundingClientRect();
  const x = (clientX - r.left) / r.width;
  return leftBoundT(state) + Math.max(0, Math.min(1, x)) * visibleDur(state);
}

export function xPctFromTime(state, t) {
  const vd = visibleDur(state);
  if (!vd) return 0;
  return ((t - leftBoundT(state)) / vd) * 100;
}

/**
 * Apply a wheel-zoom anchored at the cursor — mutates `state.zoomLevel`
 * and `state.panOffsetT` so the time under the cursor stays put.
 */
export function applyWheelZoom(state, stripEl, clientX, deltaY) {
  const dur = state.duration || 0;
  if (!dur) return;
  const tAtCursor = timeFromX(state, stripEl, clientX);
  const factor    = deltaY > 0 ? 0.85 : 1.18;        // wheel down = zoom out
  state.zoomLevel = Math.max(1, Math.min(200, (state.zoomLevel || 1) * factor));
  const newVd = dur / state.zoomLevel;
  const rect  = stripEl.getBoundingClientRect();
  const xRel  = (clientX - rect.left) / rect.width;
  state.panOffsetT = Math.max(0, Math.min(dur - newVd, tAtCursor - newVd * xRel));
}

export function applyWheelPan(state, deltaY) {
  const dur = state.duration || 0;
  if (!dur) return;
  const vd = visibleDur(state);
  state.panOffsetT = Math.max(0, Math.min(dur - vd, (state.panOffsetT || 0) + (deltaY / 100) * (vd * 0.1)));
}

/**
 * Auto-pan during playback so the cursor stays inside the visible window.
 * Returns true if `panOffsetT` was changed.
 */
export function autoPanDuringPlayback(state, currentTime) {
  if ((state.zoomLevel || 1) <= 1) return false;
  const vd    = visibleDur(state);
  const leftT = leftBoundT(state);
  if (currentTime > leftT + vd * 0.85) {
    state.panOffsetT = Math.max(0, Math.min(state.duration - vd, currentTime - vd * 0.5));
    return true;
  }
  if (currentTime < leftT + vd * 0.15 && leftT > 0) {
    state.panOffsetT = Math.max(0, currentTime - vd * 0.5);
    return true;
  }
  return false;
}
